import {
    addDays,
    eachDayOfInterval,
    endOfDay,
    isEqual,
    parseISO,
    startOfDay,
    startOfToday,
    startOfYesterday,
    subDays
} from "date-fns";
import {DateFormatter} from "../services/date";
import GeoService from "../services/geo";
import Guest from "./Guest";
import BookingItem from "./BookingItem";
import CheckIn from "./CheckIn";
import BookingCondition from "./BookingCondition";
import BookingConfirmationDocument from "./BookingConfirmationDocument";
import BookingFinance from "./BookingFinance";

/*
 * A full version of a Booking object
 */
export default class Booking {

    constructor(apiResponse) {
        this.id = apiResponse.id;
        this.codeNumber = apiResponse.codeNumber;
        this.email = apiResponse.email;
        this.name = apiResponse.name;
        this.surname = apiResponse.surname;
        this.nationality = GeoService.COUNTRIES.find(c => c.code === apiResponse.nationality);
        this.adults = apiResponse.adults;
        this.children = apiResponse.children;
        this.petsNumber = apiResponse.petsNumber;
        this.checkOutAt = apiResponse.checkOutAt;
        this.checkinAt = apiResponse.checkinAt ? parseISO(apiResponse.checkinAt) : null;
        this.onlineCheckinAt = apiResponse.onlineCheckinAt ? parseISO(apiResponse.onlineCheckinAt) : null;
        this.checkinEmailSentAt = apiResponse.checkinEmailSentAt ? parseISO(apiResponse.checkinEmailSentAt) : null;
        this.guestNotes = apiResponse.guestNotes;
        this.hostNotes = apiResponse.hostNotes;
        this.token = apiResponse.token;
        this.expectedCheckinAt = apiResponse.expectedCheckinAt;
        this.guests = apiResponse.guestDocuments ? apiResponse.guestDocuments.map(g => new Guest(g)).sort((a, b) => a.createdAt - b.createdAt) : [];

        this.bookingItems = apiResponse.bookingItems ? apiResponse.bookingItems.map(i => new BookingItem(i)) : [];
        this.alloggiatiDocumentsRequired = apiResponse.alloggiatiDocumentsRequired ? apiResponse.alloggiatiDocumentsRequired : 1;
        this.checkIns = apiResponse.checkIns ? apiResponse.checkIns.map(c => new CheckIn(c)) : [];
        this.bookingCondition = apiResponse.bookingCondition ? new BookingCondition(apiResponse.bookingCondition) : null;
        this.bookingConfirmationDocument = apiResponse.bookingConfirmationDocument ? new BookingConfirmationDocument(apiResponse.bookingConfirmationDocument) : null;
        this.bookingFinance = new BookingFinance(apiResponse.bookingFinance ?? {});
    }

    clone() {
        const booking = new Booking({});
        booking.id = this.id;
        booking.codeNumber = this.codeNumber;
        booking.email = this.email;
        booking.name = this.name;
        booking.surname = this.surname;
        booking.nationality = this.nationality;
        booking.adults = this.adults;
        booking.children = this.children;
        booking.petsNumber = this.petsNumber;
        booking.checkOutAt = this.checkOutAt;
        booking.checkinAt = this.checkinAt;
        booking.onlineCheckinAt = this.onlineCheckinAt;
        booking.checkinEmailSentAt = this.checkinEmailSentAt;
        booking.guestNotes = this.guestNotes;
        booking.hostNotes = this.hostNotes;
        booking.token = this.token;
        booking.expectedCheckinAt = this.expectedCheckinAt;
        booking.guests = this.guests.map(g => g.clone());
        booking.bookingItems = this.bookingItems.map(i => i.clone());
        booking.alloggiatiDocumentsRequired = this.alloggiatiDocumentsRequired;
        booking.checkIns = this.checkIns.map(c => c.clone());
        booking.bookingCondition = this.bookingCondition ? this.bookingCondition.clone() : null;
        booking.bookingConfirmationDocument = this.bookingConfirmationDocument ? this.bookingConfirmationDocument.clone() : null;
        booking.bookingFinance = this.bookingFinance ? this.bookingFinance.clone() : null;
        return booking;
    }

    /*
     * ASSUMPTION: The arrival date is the minimum arrival of its bookingItem
     */
    getArrival() {
        return this.bookingItems.reduce(
            (acc, x) => x.arrival < acc ? x.arrival : acc,
            this.bookingItems.length > 0 ? this.bookingItems[0].arrival : null);
    }

    /*
     * ASSUMPTION: The departure date is the maximum departure of its bookingItem
     */
    getDeparture() {
        return this.bookingItems.reduce(
            (acc, x) => x.departure > acc ? x.departure : acc,
            this.bookingItems.length > 0 ? this.bookingItems[0].departure : null);
    }

    getFullName(fallbackName = null) {
        const nameParts = [this.surname, this.name].filter(x => !!x)
        return nameParts.length > 0 ? nameParts.join(" ") : fallbackName;
    }

    getAdultLabel() {
        return `${this.adults} Adult${this.adults > 1 ? 'i' : 'o'}`;
    }

    getChildrenLabel() {
        return `${this.children} Bambin${this.children > 1 ? 'i' : 'o'}`;
    }

    getPeopleLabel() {
        return `${this.getAdultLabel()}${this.children > 0 ? `, ${this.getChildrenLabel()}` : ''}`;
    }

    getExpectedArrivalLabel() {
        if (this.expectedCheckinAt) {
            const checkInHour = Number.parseInt(this.expectedCheckinAt.split(":")[0]);
            return `${checkInHour}:00 - ${checkInHour + 2}:00`;
        } else {
            return "Non specificato";
        }
    }

    getNumberOfPeople() {
        return this.adults + this.children;
    }

    getNumberOfNights() {
        const arrival = startOfDay(this.getArrival());
        const departure = startOfDay(this.getDeparture());

        // Need to round because of daylight saving time changes that can create differences of hours
        const nights = Math.round((departure - arrival) / (1000 * 60 * 60 * 24));

        // Ensure non-negative number of nights
        return Math.max(0, nights);
    }

    getCheckInOnlineUrl() {
        return `https://guest.livignoitaly.it/check-in/${this.token}`;
    }

    getAllowedArrivalDates() {
        const today = startOfToday();
        const yesterday = startOfYesterday();

        const minDate = startOfDay(this.getArrival());
        const departureDate = startOfDay(this.getDeparture());
        const maxDate = today < departureDate ? today : subDays(departureDate, 1);

        return eachDayOfInterval({start: minDate, end: maxDate}).map((d) => ({
            date: d,
            label: isEqual(d, today) ? `Oggi, ${DateFormatter.longString(d)}` :
                (
                    isEqual(d, yesterday) ? `Ieri, ${DateFormatter.longString(d)}` : DateFormatter.longString(d)
                ),
            value: DateFormatter.onlyDateISOString(d)
        }))
    }

    getAllowedDepartureDates({allowFutureDates} = {allowFutureDates: false}) {
        const today = startOfToday();
        const yesterday = startOfYesterday();

        const firstDay = startOfDay(addDays(this.getArrival(), 1));
        const lastDay = startOfDay(this.getDeparture());

        const allowedDates = eachDayOfInterval(
            {start: firstDay, end: lastDay}
        ).reverse().filter(d => allowFutureDates || d <= today);

        return allowedDates.map((d) => ({
            date: d,
            label: isEqual(d, today) ? `Oggi, ${DateFormatter.longString(d)}` :
                (
                    isEqual(d, yesterday) ? `Ieri, ${DateFormatter.longString(d)}` : DateFormatter.longString(d)
                ),
            value: DateFormatter.onlyDateISOString(d)
        }))
    }

    getGuestReadyForCheckIn() {
        return this.guests.filter(g => g.guestStays.length === 0 || g.guestStays.every(gs => !!gs.departure))
    }

    getNotCheckedOutGuestStays() {
        return this.guests.flatMap(g => g.guestStays.filter(s => !!s.arrival && !s.departure));
    }

    guestWithDocuments() {
        return this.guests.filter(g => g.hasIdDocument());
    }

    hasAtLeastOneIdDocument() {
        return this.guestWithDocuments().length >= 1;
    }

    hasRequiredAlloggiatiDocuments() {
        return this.guestWithDocuments().length >= this.alloggiatiDocumentsRequired
    }

    isOnlineCheckInDone() {
        return !!this.onlineCheckinAt;
    }

    isCheckInDone() {
        return this.checkIns.length > 0;
    }

    checkOutEnabled() {
        const today = startOfToday()
        const notCheckedOutGuests = this.getNotCheckedOutGuestStays();
        return today > startOfDay(this.getArrival()) && today <= startOfDay(this.getDeparture()) && notCheckedOutGuests.length > 0
    }

    checkInOpen() {
        const today = new Date();
        // The check in is open from the arrival date to the departure date (inclusive), so even the last day
        // the user can make a check in the past. The allowed dated are managed in the form.
        return today >= startOfDay(this.getArrival()) && today <= endOfDay(this.getDeparture())
    }

    todayIsTheLastDay() {
        const today = startOfToday();
        return isEqual(today, startOfDay(this.getDeparture()));
    }

    beforeCheckInOpen() {
        const today = startOfToday();
        return today < startOfDay(this.getArrival());
    }

    isDeleteAllowed() {
        return !this.isCheckInDone() && (this.getArrival() >= startOfToday()) && !this.isOnlineCheckInDone() && this.bookingFinance.payments.length === 0;
    }

    isInThePast() {
        return this.getDeparture() < startOfToday();
    }

    getGuestsSummary(skipPets = false) {
        const text = Array();
        if (this.adults === 1) {
            text.push('1 Adulto')
        } else {
            text.push(`${this.adults} Adulti`)
        }

        if (this.children === 1) {
            text.push('1 Bambino')
        } else if (this.children > 1) {
            text.push(`${this.children} Bambini`)
        }

        if (!skipPets) {
            if (this.petsNumber === 1) {
                text.push('1 Animale')
            } else if (this.petsNumber > 1) {
                text.push(`${this.petsNumber} Animali`)
            }
        }

        return text.join(', ')
    }

    isEditable() {
        return !this.isInThePast()
    }

    isPeriodEditable() {
        return !this.isInThePast()
    }
}